import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, StarInput, TextArea, TextField, Chip, Button, SvgBase } from 'components/ds';
import { PhotoPicker } from './PhotoPicker';
import styles from './VisitPhotoWrite.module.css';

const MAX_PHOTOS = 5;
const MAX_TAGS = 5;
const MAX_LEN = 500;

/** 추천 태그 — 참고 앱은 서버가 내려줍니다. 붙기 전까지는 이 목록을 씁니다. */
export const SUGGESTED_TAGS = ['MOOD', 'TASTE', 'PRICE', 'SEAT', 'PARKING', 'QUIET'] as const;

export interface VisitPhotoDraft {
    rating: number;
    content: string;
    tags: string[];
    photos: File[];
}

/**
 * 방문 사진 올리기.
 *
 * 참고 앱의 방문 사진 등록과 같은 차례입니다 — 사진 → 태그 → 후기 500자 → 등록.
 * 태그는 추천 태그를 고르거나 직접 써 넣습니다(최대 5개).
 *
 * 참고 앱은 사진이 있어야 등록됩니다. 모이는 별점이 있어야 등록됩니다 —
 * 이 별점이 장소 목록의 외부 평점을 대신하기 때문에, 사진만 있고 별점이 없으면
 * 정작 필요한 값이 안 모입니다. 사진과 글은 없어도 됩니다.
 */
export const VisitPhotoWrite = ({
    open, onClose, placeName, onSubmit, submitting, suggestedTags, initial, editing,
}: {
    open: boolean;
    onClose: () => void;
    placeName: string;
    onSubmit: (draft: VisitPhotoDraft) => void;
    submitting?: boolean;
    /** 서버가 내려주는 추천 태그. 없으면 기본 목록을 씁니다 */
    suggestedTags?: string[];
    /** 고칠 때 채워 넣을 지금 값. 없으면 빈 칸으로 엽니다 */
    initial?: { rating?: number; content?: string | null; tags?: string[] | null };
    /** 고치는 중인가 — 사진 칸을 감춥니다. 수정 API 가 사진을 받지 않습니다 */
    editing?: boolean;
}) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState(0);
    const [content, setContent] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [tagText, setTagText] = useState('');
    const [photos, setPhotos] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);

    /* 시트를 열 때 채워 넣습니다 — 고치는 중이면 지금 값, 새로 쓰면 빈 칸.
       열려 있는 동안에는 initial 을 다시 보지 않습니다. 목록이 새로 들어올 때마다
       보면 쓰던 내용을 덮어씁니다. */
    useEffect(() => {
        if (!open) return;
        setRating(initial?.rating ?? 0);
        setContent(initial?.content ?? '');
        setTags(initial?.tags ?? []);
        setTagText('');
        setPhotos([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 미리보기 URL 은 만든 쪽이 거둡니다
    useEffect(() => {
        const urls = photos.map(f => URL.createObjectURL(f));
        setPreviews(urls);
        return () => { urls.forEach(URL.revokeObjectURL); };
    }, [photos]);

    const recommended = suggestedTags ?? SUGGESTED_TAGS.map(k => t(`placeReview.tag.${k}`));

    const toggleTag = (tag: string) => setTags(v => (
        v.includes(tag) ? v.filter(x => x !== tag) : (v.length >= MAX_TAGS ? v : [...v, tag])
    ));

    const addTypedTag = () => {
        const tag = tagText.trim().replace(/^#/, '');
        if (!tag || tags.includes(tag) || tags.length >= MAX_TAGS) return;
        setTags(v => [...v, tag]);
        setTagText('');
    };

    const ratingLabels = [1, 2, 3, 4, 5].map(n => t(`placeReview.ratingLabel.${n}`));

    return (
        <Sheet open={open} onClose={onClose}
               title={t(editing ? 'visitPhoto.editTitle' : 'visitPhoto.writeTitle')}
               closeLabel={t('common.close')}>
            <div className={styles.body}>
                <p className={styles.place}>{placeName}</p>

                <div className={styles.stars}>
                    <StarInput value={rating} onChange={setRating} labels={ratingLabels} />
                </div>

                {/* 사진 — 가로로 넘기고, 각 장에 지우기가 붙습니다.
                    고칠 때는 감춥니다. 수정 API 가 사진을 받지 않으니 골라도
                    올라가지 않습니다 — 올라갈 것처럼 보이면 안 됩니다. */}
                {editing ? (
                    <p className={styles.note}>{t('visitPhoto.editPhotoNote')}</p>
                ) : (
                <div className={styles.photos}>
                    {previews.map((url, i) => (
                        <div key={url} className={styles.photo}>
                            <img src={url} alt="" />
                            <button
                                type="button"
                                className={styles.remove}
                                aria-label={t('common.delete')}
                                onClick={() => setPhotos(v => v.filter((_, n) => n !== i))}
                            >
                                <SvgBase size={14} strokeWidth={2}>
                                    <path d="M6 6l12 12M18 6L6 18" />
                                </SvgBase>
                            </button>
                        </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                        <div className={styles.picker}>
                            <PhotoPicker
                                multiple
                                label={`${photos.length}/${MAX_PHOTOS}`}
                                onPick={(files) => setPhotos(v => [...v, ...files].slice(0, MAX_PHOTOS))}
                            />
                        </div>
                    )}
                </div>
                )}

                {/* 태그 — 고른 것 · 추천 · 직접 입력 */}
                <div className={styles.tagBlock}>
                    {tags.length > 0 && (
                        <div className={styles.chosen}>
                            {tags.map(tag => (
                                <button key={tag} type="button" className={styles.chosenTag} onClick={() => toggleTag(tag)}>
                                    <span>#{tag}</span>
                                    <SvgBase size={12} strokeWidth={2}><path d="M6 6l12 12M18 6L6 18" /></SvgBase>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className={styles.tags}>
                        {recommended.map(tag => (
                            <Chip
                                key={tag}
                                label={tag}
                                selected={tags.includes(tag)}
                                disabled={!tags.includes(tag) && tags.length >= MAX_TAGS}
                                onSelect={() => toggleTag(tag)}
                            />
                        ))}
                    </div>
                    <div className={styles.tagInput}>
                        <TextField
                            value={tagText}
                            maxLength={20}
                            placeholder={t('visitPhoto.tagPlaceholder', { max: MAX_TAGS })}
                            onChange={(e) => setTagText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTypedTag(); } }}
                        />
                        <Button
                            variant="secondary"
                            disabled={!tagText.trim() || tags.length >= MAX_TAGS}
                            onClick={addTypedTag}
                        >
                            {t('visitPhoto.tagAdd')}
                        </Button>
                    </div>
                </div>

                <TextArea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    maxLength={MAX_LEN}
                    showCount
                    rows={5}
                    placeholder={t('visitPhoto.contentPlaceholder')}
                />

                <Button
                    block
                    disabled={rating === 0 || submitting}
                    onClick={() => onSubmit({ rating, content: content.trim(), tags, photos })}
                >
                    {t(editing ? 'common.save' : 'common.register')}
                </Button>
            </div>
        </Sheet>
    );
};
